import { Input } from '@ibsys/kit';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib';
import { Tooltip } from './Tooltip';

type Props = {
  value: string;
  /**
   * Название действия правки: доступное имя названия, по которому его правят. Называет предмет
   * («Изменить название компетенции»), потому что правка названия в разных местах правит разное;
   * приходит от места применения и потому переведено там же, где остальные его строки.
   */
  label: string;
  /** Текст на месте пустого названия; не задан — общий заполнитель */
  placeholder?: string;
  disabled?: boolean;
  onSave?: (value: string) => void;
  textClassName?: string;
  inputClassName?: string;
  /** Фиксированная ширина поля ввода вместо авто-расширения по контенту */
  fixedInputWidth?: boolean;
  /**
   * Название всё время показано полем ввода — заданное с самим названием, пустое с заполнителем, —
   * а не текстом, который становится полем по нажатию: видно, что название правится здесь
   */
  isShownAsInput?: boolean;
  /** Поле, появившееся с пустым названием, сразу принимает ввод */
  isEmptyInputAutoFocused?: boolean;
};

export const EditableTitle: FC<Props> = (props) => {
  const {
    value,
    label,
    placeholder,
    disabled = false,
    onSave,
    textClassName,
    inputClassName,
    fixedInputWidth = false,
    isShownAsInput = false,
    isEmptyInputAutoFocused = false,
  } = props;

  const { t } = useTranslation('common');
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isEditing) {
      const timeoutId = setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 10);
      return () => clearTimeout(timeoutId);
    }
  }, [isEditing]);

  // Место названия занято всегда: у пустого названия иначе не остаётся ничего, по чему в него попасть
  const emptyText = placeholder || t('untitled');
  const displayValue = value?.trim() || emptyText;
  const isInputShown = isShownAsInput && !disabled;

  const handleTitleClick = () => {
    if (!disabled) {
      setIsEditing(true);
    }
  };

  const handleBlur: React.FocusEventHandler<HTMLInputElement> = (event) => {
    setIsEditing(false);
    const trimmedValue = event.target.value.trim();
    // Поле остаётся на месте и показывает принятое название: одни пробелы в нём не должны прятать
    // заполнитель, а пробелы по краям — расходиться с названием, отданным владельцу
    event.target.value = trimmedValue;
    if (trimmedValue !== value && onSave) {
      onSave(trimmedValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
    if (e.key === 'Escape') {
      // Постоянное поле с отказом не исчезает — отказ возвращает в нём название, бывшее до правки
      if (isInputShown) {
        e.currentTarget.value = value ?? '';
        e.currentTarget.blur();
      }
      setIsEditing(false);
    }
  };

  if (isEditing || isInputShown) {
    const inputSizeClass = fixedInputWidth ? 'min-w-72 w-72 max-w-[700px]' : 'field-sizing-content max-w-[700px]';
    return (
      <div className="inline-flex">
        <Input
          ref={inputRef}
          defaultValue={value}
          placeholder={isInputShown ? emptyText : undefined}
          // У постоянного поля нет текста-кнопки с названием правки — его несёт само поле
          aria-label={isInputShown ? label : undefined}
          // Курсор ставится только полю, появившемуся без названия: заданное название его не захватывает,
          // а опустевшее по ходу правки поле и так в фокусе
          autoFocus={isEmptyInputAutoFocused && isInputShown && !value?.trim()}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          size="lg"
          classNames={{
            input: `${inputSizeClass} ${inputClassName ?? ''}`,
          }}
        />
      </div>
    );
  }

  return (
    <Tooltip tooltip={displayValue} onlyWhenTruncated>
      <span
        role="button"
        tabIndex={disabled ? -1 : 0}
        className={cn(textClassName, !disabled && 'cursor-pointer')}
        onClick={handleTitleClick}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            handleTitleClick();
          }
        }}
        // Название правки — только у названия, которое правят: у показанного для чтения имени
        // действия нет, и своё название оно называет само
        aria-label={disabled ? undefined : label}
      >
        {displayValue}
      </span>
    </Tooltip>
  );
};
